import clsx from "clsx";
import moment from "moment";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  MdAddCard,
  MdCheck,
  MdClose,
  MdCreditCard,
  MdDoneAll,
  MdError,
  MdOutlineCompareArrows,
} from "react-icons/md";
import CollapsibleBlock from "src/components/CollapsibleBlock/CollapsibleBlock";
import Modal from "src/components/Modal/Modal";
import { v4 as uuidv4 } from "uuid";
import { ALL_TRANSACTION_STATUSES } from "gastos-shared";

import CustomSelect, {
  ICustomSelectOption,
} from "src/components/Select/CustomSelect";
import TextInput from "src/components/TextInput/TextInput";
import { ETransactionType } from "src/helpers/constants";
import {
  formatCurrency,
  formatFileSize,
  getTotalByTransactionType,
  showNotification,
  transformToSelectOptions,
} from "src/helpers/utils";
import useCollapsibles from "src/hooks/useCollapsibles";
import useCurrentUser from "src/hooks/useCurrentUser";
import useMobile from "src/hooks/useMobile";
import MoneyTransferAttachments from "./MoneyTransferAttachments";
import { addTransaction } from "src/api/transactions";
import useModalsStore from "src/store/modalsStore";
import useCache from "src/hooks/useCache";
import TransactionAttachmentsLightbox from "./TransactionAttachmentsLightbox";
import ConfirmDialog from "src/components/ConfirmDialog/ConfirmDialog";
import { sortBy } from "lodash";
import BankCard from "src/components/BankCard/BankCard";
import useAllTransactions from "src/hooks/useAllTransactions";
import useCurrencies from "src/hooks/useCurrencies";
import { useNavigate } from "react-router-dom";
import temporalStore from "src/store/temporalStore";
import TransactionStatusMessage from "../TransactionStatusMessage/TransactionStatusMessage";
import useColors from "src/hooks/useColors";
import InfoBlock from "src/components/InfoBlock/InfoBlock";
import useUiPreferences from "src/store/useUiPreferences";
import { EBadgeKind } from "src/components/Badge/Badge";
import { ICON_SIZES } from "src/constants/icons";
import { EButtonKind } from "src/components/Button/Button.types";

interface IMoneyTransferModal {
  onClose: () => void;
  disableFields?: boolean;
  onSubmit: (values: any) => void;
}

const MoneyTransferModal: React.FC<IMoneyTransferModal> = ({
  onClose,
  disableFields = false,
  onSubmit,
}) => {
  const navigate = useNavigate();
  const { getAccountColors } = useColors();
  const { clearCache } = useCache();
  const { allActiveTransactions } = useAllTransactions();
  const { getCurrencyDataByCode } = useCurrencies();
  const { preselectedAccountForTransfer } = temporalStore();
  const {
    setShowMoneyTransferModal,
    setShowAccountsSettingsModal,
    setShowCreateNewAccountModal,
  } = useModalsStore();
  const { t } = useTranslation(["modals", "common"]);
  const {
    getAccountById,
    allEnabledPaymentMethods,
    enabledAccountsList,
    allPaymentMethods,
    allUserAccounts,
  } = useCurrentUser();
  const accountsUiPreferences = useUiPreferences((s) => s.accounts);

  const thumbnailsRef = useRef<any>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);

  const [attachmentsGalleryIsOpen, setAttachmentsGalleryIsOpen] =
    useState<boolean>(false);

  const { collapsiblePreferences, updateCollapsible } = useCollapsibles();
  const [clickedAttachmentIndex, setClickedAttachmentIndex] =
    useState<number>(0);

  const [date, setDate] = useState<string>(moment().format("yyyy-MM-DD"));
  const [time, setTime] = useState<string>(moment().format("HH:mm"));
  const [account, setAccount] = useState<string>("");
  const [originAccount, setOriginAccount] = useState<string>(
    preselectedAccountForTransfer || "",
  );
  const [destinationAccount, setDestinationAccount] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [amountToTransfer, setAmountToTransfer] = useState<string>("");
  const [paymentMethod, setPaymentMethod] = useState<string>("CARD");
  const { isMobile } = useMobile();
  const [receiptNumber, setReceiptNumber] = useState<string>("");
  const [filesToAttach, setFilesToAttach] = useState<Array<any>>([]);
  const [isTransferringMoney, setIsTransferringMoney] =
    useState<boolean>(false);

  const originAccountData = useMemo(() => {
    if (!originAccount) return null;

    return getAccountById(originAccount);
  }, [originAccount]);

  const accountIncome = useMemo(
    () =>
      getTotalByTransactionType({
        type: ETransactionType.INCOME,
        transactions: allActiveTransactions,
        account: originAccount,
      }),
    [allActiveTransactions, originAccount],
  );

  useEffect(() => {
    setOriginAccount(preselectedAccountForTransfer);
  }, []);

  const accountExpenses = useMemo(
    () =>
      getTotalByTransactionType({
        type: ETransactionType.EXPENSE,
        transactions: allActiveTransactions,
        account: originAccount,
      }),
    [allActiveTransactions, originAccount],
  );

  const paymentMethods = useMemo(() => {
    return sortBy(
      transformToSelectOptions({
        data: allEnabledPaymentMethods,
        labelField: "name",
        valueField: "id",
      }),
      ["label"],
    );
  }, [allEnabledPaymentMethods]);

  const accountsToShow = useMemo(() => {
    if (accountsUiPreferences.showInactiveAccounts) {
      return allUserAccounts;
    }

    return enabledAccountsList;
  }, [
    accountsUiPreferences.showInactiveAccounts,
    allUserAccounts,
    enabledAccountsList,
  ]);

  const originAccountsList = useMemo(() => {
    if (!accountsToShow?.length) return [];

    return (sortBy(
      transformToSelectOptions({
        data: accountsToShow,
        labelField: "name",
        valueField: "account",
      }),
      ["label"],
    ) || []) as Array<ICustomSelectOption>;
  }, [accountsToShow]);

  const destinationAccountsList = useMemo(() => {
    if (!accountsToShow?.length) return [];

    return (sortBy(
      transformToSelectOptions({
        data: accountsToShow?.filter?.(
          (account: any) =>
            account?.account !== originAccount &&
            account?.currency === originAccountData?.currency,
        ),
        labelField: "name",
        valueField: "account",
      }),
      ["label"],
    ) || []) as Array<ICustomSelectOption>;
  }, [accountsToShow, originAccount]);

  // useEffect(() => {
  //   if (
  //     !showInactiveAccounts &&
  //     originAccount &&
  //     !some(originAccountsList, { account: originAccount })
  //   ) {
  //     setOriginAccount("");
  //   }
  // }, [originAccountsList, showInactiveAccounts]);

  // useEffect(() => {
  //   if (
  //     !showInactiveAccounts &&
  //     destinationAccount &&
  //     !some(destinationAccountsList, { account: destinationAccount })
  //   ) {
  //     setDestinationAccount("");
  //   }
  // }, [destinationAccountsList, showInactiveAccounts]);

  useEffect(() => {
    setDestinationAccount("");
  }, [originAccount]);

  const totalAvailableMoney = useMemo(() => {
    return accountIncome - accountExpenses;
  }, [accountIncome, accountExpenses]);

  const dataIsValid = useMemo(() => {
    return (
      !!date &&
      !!originAccount &&
      !!destinationAccount &&
      !!paymentMethod &&
      !!amountToTransfer
    );
  }, [
    date,
    originAccount,
    destinationAccount,
    amountToTransfer,
    paymentMethod,
  ]);

  const hasFunds = totalAvailableMoney > 0;

  const amountIsGreaterThanFunds = useMemo(() => {
    return Number(amountToTransfer) > totalAvailableMoney;
  }, [amountToTransfer, totalAvailableMoney]);

  const saveIsEnabled =
    (accountsUiPreferences.allowTransfersWithNoFunds
      ? true
      : totalAvailableMoney) &&
    !!originAccount &&
    !!destinationAccount &&
    !!amountToTransfer &&
    !!date &&
    !!paymentMethod &&
    (accountsUiPreferences.allowTransfersWithNoFunds
      ? true
      : !amountIsGreaterThanFunds);

  const transferMoney = async () => {
    setIsTransferringMoney(true);
    try {
      const transactionsToRegister = [
        {
          type: ETransactionType.EXPENSE,
          category: "Transferencia entre cuentas",
          account: originAccountData.account,
          description: `Transacción a mi cuenta ${
            getAccountById(account)?.name ||
            t("misc:strings.unknown_account_name")
          }`,
          transactionId: uuidv4(),
        },
        {
          type: ETransactionType.INCOME,
          category: "Transferencia entre cuentas",
          account: destinationAccountData.account,
          description: `Transacción de mi cuenta ${
            originAccountData?.name || t("misc:strings.unknown_account_name")
          }`,
          transactionId: uuidv4(),
        },
      ];

      const results = await Promise.allSettled(
        transactionsToRegister?.map?.(
          async ({ category, type, account, description, transactionId }) => {
            return await addTransaction({
              transactionId,
              fieldsToSave: {
                invoice: transactionId,
                date,
                time,
                documentNumber: "",
                payTo: "Yo mismo",
                notes: notes?.trim?.() || "",
                taxes: 0,
                account,
                details: [],
                description,
                subTotal: amountToTransfer,
                type,
                discount: 0,
                category,
                status: ALL_TRANSACTION_STATUSES.PAID,
                paymentMethod,
                total: amountToTransfer,
                tags: [],
              },
              files: filesToAttach,
            });
          },
        ),
      );

      const successfulResults = results
        .filter((r) => r.status === "fulfilled")
        .map((r) => (r as PromiseFulfilledResult<any>).value.data);

      if (successfulResults?.length === transactionsToRegister?.length) {
        await clearCache();
        setShowConfirmDialog(false);
        setShowMoneyTransferModal(false);
        showNotification({
          message: "Fondos transferidos correctamente",
          kind: "success",
        });
      } else {
        showNotification({
          message: "Ocurrió un al transferir los fondos",
          kind: "error",
        });
      }
    } catch (e) {
      showNotification({
        message: "Ocurrió un al transferir los fondos",
        kind: "error",
      });
    } finally {
      setIsTransferringMoney(false);
    }
  };

  const unifiedAttachmentsList = useMemo(() => {
    return [
      ...(filesToAttach?.map?.((theFile: any) => {
        return {
          name: theFile.name,
          size: theFile.size,
          formattedSize: formatFileSize(theFile.size),
          type: theFile.type,
          typeFormatted:
            theFile?.type?.split?.("/")?.[1]?.toUpperCase?.() ||
            t("misc:strings.unknown_file_type"),
          isSaved: false,
          thumbnailUrl: theFile?.preview,
          fileUrl: theFile?.preview,
        };
      }) || []),
    ];
  }, [filesToAttach]);

  const attachmentSlides: any[] = useMemo(() => {
    return (
      unifiedAttachmentsList?.map?.((attachment: any) => {
        return {
          type: attachment?.type === "application/pdf" ? undefined : "image",
          src: attachment?.fileUrl,
        };
      }) || []
    );
  }, [unifiedAttachmentsList]);

  const destinationAccountData = useMemo(() => {
    if (destinationAccount) {
      return getAccountById(destinationAccount);
    }

    return null;
  }, [destinationAccount, getAccountById]);

  const availableMoney = useCallback(
    (account: any) => {
      const income = getTotalByTransactionType({
        type: ETransactionType.INCOME,
        transactions: allActiveTransactions,
        account: account,
      });
      const expenses = getTotalByTransactionType({
        type: ETransactionType.EXPENSE,
        transactions: allActiveTransactions,
        account: account,
      });

      return income - expenses;
    },
    [allActiveTransactions],
  );

  const showAccountSettingsMessage = useMemo(() => {
    if (originAccount && originAccountData) {
      return !hasFunds || amountIsGreaterThanFunds;
    }

    return false;
  }, [originAccount, originAccountData, hasFunds, amountIsGreaterThanFunds]);

  const currencySymbol = useMemo(() => {
    if (originAccount && originAccountData) {
      return (
        getCurrencyDataByCode({
          code: getAccountById(originAccount)?.currency,
        })?.symbol_native || ""
      );
    }

    return "?";
  }, [originAccount, originAccountData]);

  const NoCardSelected = () => {
    if (isMobile) return null;

    return (
      <div
        className={clsx(["h-full", "flex", "items-center", "justify-center"])}
      >
        <MdCreditCard
          size={ICON_SIZES.XL}
          className={clsx(["text-gray-200"])}
        />
      </div>
    );
  };

  return (
    <>
      <Modal
        onEscape={() => {
          if (!isTransferringMoney) {
            onClose();
          }
        }}
        className={clsx(["!w-full", "md:!max-w-[700px]"])}
        closeOnOverlayClick={!isTransferringMoney}
      >
        <Modal.Header
          title={t("modals:transfer_funds.title")}
          onClose={() => {
            if (!isTransferringMoney) {
              onClose();
            }
          }}
          // buttons={[
          //   {
          //     tooltip: t("Opciones"),
          //     onClick: () => {
          //       setShowAccountsSettingsModal(true);
          //     },
          //     icon: <MdOutlineTune size={ICON_SIZES.SM} />,
          //     kind: EButtonKind.NORMAL,
          //   },
          // ]}
        />
        <Modal.Body>
          <div
            className={clsx([
              "flex",
              "w-full",
              "flex-col",
              "gap-4",
              "md:gap-6",
              "text-sm",
              "md:text-sm",
              "pb-2",
            ])}
          >
            <div
              className={clsx([
                "grid",
                "grid-cols-1",
                "md:grid-cols-[1fr_60px_1fr]",
                "gap-4",
                "md:gap-2",
              ])}
            >
              <div
                className={clsx([
                  "flex",
                  "flex-col",
                  "w-full",
                  "gap-4",
                  "md:gap-6",
                ])}
              >
                {originAccount ? (
                  <BankCard
                    disabled={originAccountData?.settings?.disabled}
                    account={originAccountData.account}
                    currency={originAccountData.currency}
                    image={originAccountData.settings?.picture?.thumbnailUrl}
                    bank={
                      originAccountData.bank ||
                      t("misc:strings.unknown_bank_name")
                    }
                    name={originAccountData.name}
                    number={
                      originAccountData.number ||
                      t("pages:account.unknown_account_number")
                    }
                    customStyles={{
                      width: "100%",
                      ...getAccountColors(originAccountData?.account).string,
                    }}
                    available={availableMoney(originAccountData.account)}
                  />
                ) : (
                  <NoCardSelected />
                )}
                <CustomSelect
                  label={t("modals:transfer_funds.collapsibles.data.origin")}
                  options={originAccountsList}
                  value={originAccount}
                  useValueAsLabel={false}
                  onChange={(e: any) => setOriginAccount(e?.value || "")}
                  required
                  placeholder={t(
                    "modals:transfer_funds.collapsibles.data.origin_placeholder",
                  )}
                />
              </div>
              <div
                className={clsx([
                  "hidden",
                  "md:flex",
                  "items-center",
                  "justify-center",
                  "text-xs",
                  "shrink-0",
                ])}
              >
                <MdOutlineCompareArrows
                  size={isMobile ? ICON_SIZES.MD : ICON_SIZES.MD_LG}
                  className="animate-bounce"
                />
              </div>
              <div
                className={clsx([
                  "flex",
                  "flex-col",
                  "w-full",
                  "gap-4",
                  "md:gap-6",
                  {
                    // "self-end": !destinationAccount,
                  },
                ])}
              >
                {destinationAccount ? (
                  <BankCard
                    disabled={destinationAccountData?.settings?.disabled}
                    image={
                      destinationAccountData.settings?.picture?.thumbnailUrl
                    }
                    account={destinationAccountData.account}
                    currency={destinationAccountData.currency}
                    bank={
                      destinationAccountData.bank ||
                      t("misc:strings.unknown_bank_name")
                    }
                    name={destinationAccountData.name}
                    number={
                      destinationAccountData.number ||
                      t("pages:account.unknown_account_number")
                    }
                    customStyles={{
                      width: "100%",
                      ...getAccountColors(destinationAccountData?.account)
                        .string,
                    }}
                    available={availableMoney(destinationAccountData.account)}
                  />
                ) : (
                  <NoCardSelected />
                )}
                <CustomSelect
                  label={t(
                    "modals:transfer_funds.collapsibles.data.destination",
                  )}
                  options={destinationAccountsList}
                  value={destinationAccount}
                  useValueAsLabel={false}
                  onChange={(e: any) => setDestinationAccount(e?.value)}
                  required
                  placeholder={t(
                    "modals:transfer_funds.collapsibles.data.destination_placeholder",
                  )}
                />
              </div>
            </div>
            <div>
              <InfoBlock
                message={t("modals:transfer_funds.message")}
                displayMobileIcon
              />
            </div>
            <div
              className={clsx([
                "flex",
                "w-full",
                "flex-row",
                "justify-start",
                "items-center",
                "gap-1",
                "hidden",
              ])}
            >
              <div className={clsx(["flex", "flex-col"])}>
                <p>
                  {t("modals:transfer_funds.tranfer_limit", {
                    amount: formatCurrency({
                      value: totalAvailableMoney,
                      currency: currencySymbol,
                    }),
                  })}
                </p>
                {amountIsGreaterThanFunds && (
                  <p className="warning-text">
                    {t("modals:transfer_funds.amount_is_higher")}
                  </p>
                )}
              </div>
            </div>
            <div
              className={clsx([
                "flex",
                "flex-col-reverse",
                "gap-4",
                "md:gap-6",
              ])}
            >
              <CollapsibleBlock
                title={t("modals:transfer_funds.collapsibles.data.title")}
                uppercase
                // open={collapsiblePreferences.moneyTransferDataOpen}
                blocked
                open
                onChange={(isOpen: boolean) => {
                  updateCollapsible({
                    moneyTransferDataOpen: isOpen,
                  });
                }}
                badge={
                  dataIsValid
                    ? formatCurrency({
                        value: amountToTransfer,
                        currency: currencySymbol,
                      })
                    : t("common:badges.incomplete")
                }
                hasError={!dataIsValid}
                badgeKind={dataIsValid ? EBadgeKind.INFO : EBadgeKind.DANGER}
                icon={
                  dataIsValid &&
                  (accountsUiPreferences.allowTransfersWithNoFunds
                    ? true
                    : !amountIsGreaterThanFunds) ? (
                    <MdDoneAll size={ICON_SIZES.SM} className="text-lime-400" />
                  ) : (
                    <MdError
                      size={ICON_SIZES.SM}
                      className="text-red-500 dark:text-teal-300"
                    />
                  )
                }
              >
                <div
                  className={clsx([
                    "flex",
                    "w-full",
                    "flex-col",
                    "gap-4",
                    "md:gap-6",
                  ])}
                >
                  {!isMobile && (
                    <>
                      {showAccountSettingsMessage && (
                        <TransactionStatusMessage
                          amount={amountToTransfer}
                          funds={totalAvailableMoney}
                          transactionType={ETransactionType.EXPENSE}
                          showButtons={false}
                        />
                      )}
                    </>
                  )}
                  <div
                    className={clsx([
                      "grid",
                      "grid-cols-1",
                      "md:grid-cols-3",
                      "gap-4",
                      "md:gap-6",
                    ])}
                  >
                    <TextInput
                      placeholder={t(
                        "modals:transfer_funds.collapsibles.data.date_placeholder",
                      )}
                      value={date}
                      onChange={(e) => setDate(e?.target?.value)}
                      label={t("modals:transfer_funds.collapsibles.data.date")}
                      type="date"
                      required
                    />
                    <TextInput
                      placeholder={t(
                        "modals:transfer_funds.collapsibles.data.time_placeholder",
                      )}
                      value={time}
                      onChange={(e) => setTime(e?.target?.value)}
                      label={t(
                        "pages:transaction.collapsibles.header.fields.time",
                      )}
                      type="time"
                      required
                    />
                    {isMobile &&
                      (showAccountSettingsMessage ? (
                        <TransactionStatusMessage
                          amount={amountToTransfer}
                          funds={totalAvailableMoney}
                          transactionType={ETransactionType.EXPENSE}
                          showButtons={false}
                        />
                      ) : null)}
                    <TextInput
                      required
                      onChange={(e: any) =>
                        setAmountToTransfer(e?.target?.value)
                      }
                      value={amountToTransfer}
                      label={t(
                        "modals:transfer_funds.collapsibles.data.amount",
                      )}
                      placeholder={t(
                        "modals:transfer_funds.collapsibles.data.amount_placeholder",
                      )}
                      type="number"
                      autoComplete="off"
                      currencySymbol={currencySymbol}
                    />
                  </div>
                  <div
                    className={clsx([
                      "grid",
                      "grid-cols-1",
                      "md:grid-cols-2",
                      "gap-4",
                      "md:gap-6",
                    ])}
                  >
                    <CustomSelect
                      options={paymentMethods}
                      onChange={(e: any) => {
                        setPaymentMethod(e?.value);
                      }}
                      label={t(
                        "modals:transfer_funds.collapsibles.data.method",
                      )}
                      value={paymentMethod}
                      placeholder={t(
                        "modals:transfer_funds.collapsibles.data.method_placeholder",
                      )}
                      useValueAsLabel={false}
                      required
                      helpText={
                        allEnabledPaymentMethods?.length ===
                        allPaymentMethods?.length
                          ? ""
                          : t(
                              "pages:transaction.collapsibles.payment.payment_method_help_text",
                            )
                      }
                    />
                    <TextInput
                      onChange={(e: any) => setReceiptNumber(e?.target?.value)}
                      label={t(
                        "modals:transfer_funds.collapsibles.data.voucher",
                      )}
                      value={receiptNumber}
                      placeholder={t(
                        "modals:transfer_funds.collapsibles.data.voucher_placeholder",
                      )}
                    />
                    <TextInput
                      onChange={(e: any) => setNotes(e?.target?.value)}
                      label={t("modals:transfer_funds.collapsibles.data.notes")}
                      value={notes}
                      placeholder={t(
                        "modals:transfer_funds.collapsibles.data.notes_placeholder",
                      )}
                    />
                  </div>
                </div>
              </CollapsibleBlock>

              <MoneyTransferAttachments
                attachments={[]}
                onChange={({ unsaved }) => setFilesToAttach(unsaved)}
                onClick={({
                  saved,
                  index,
                }: {
                  saved: boolean;
                  index: number;
                }) => {
                  setClickedAttachmentIndex(index);
                  setAttachmentsGalleryIsOpen(true);
                }}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer
          actions={[
            {
              text: t("common:buttons.transfer"),
              onClick: () => {
                setShowConfirmDialog(true);
              },
              kind: EButtonKind.SAVE,
              icon: <MdCheck size={ICON_SIZES.SM} />,
              isLoading: isTransferringMoney,
              disabled: !saveIsEnabled || isTransferringMoney,
              hide: !destinationAccountsList?.length,
            },
            {
              icon: <MdAddCard size={ICON_SIZES.SM} />,
              text: t("common:buttons.add"),
              onClick: () => {
                setShowMoneyTransferModal(false);
                setShowCreateNewAccountModal(true);
              },
              kind: EButtonKind.NORMAL,
              hide: destinationAccountsList?.length > 0,
            },
            {
              text: t("common:buttons.close"),
              onClick: () => {
                if (!isTransferringMoney) {
                  onClose();
                }
              },
              kind: EButtonKind.NORMAL,
              icon: <MdClose size={ICON_SIZES.SM} />,
              disabled: isTransferringMoney,
            },
          ]}
        />
      </Modal>
      {attachmentsGalleryIsOpen && (
        <TransactionAttachmentsLightbox
          slides={attachmentSlides}
          index={clickedAttachmentIndex}
          setIndex={setClickedAttachmentIndex}
          thumbnailsRef={thumbnailsRef}
          unifiedAttachmentsList={unifiedAttachmentsList}
          onClose={() => setAttachmentsGalleryIsOpen(false)}
        />
      )}
      {showConfirmDialog && (
        <ConfirmDialog
          title="Transferir"
          description="¿Deseas transferir los fondos?"
          onClose={() => setShowConfirmDialog(false)}
          onConfirm={async () => {
            await transferMoney();
          }}
          isLoading={isTransferringMoney}
        />
      )}
    </>
  );
};

export default MoneyTransferModal;
